// One shared reader over the agent's zone-KPI contract (`GET /buildings/{id}/zones/kpis-
// summary`) — käyttöaste, sisäilman laatu, lämpötila, each already resolved server-side to a
// −100…+100 score (10 unnamed steps), raw value, provenance and confidence, or an explicit
// not-ready. Sisäilmaolosuhteet (ZoneTile) paints the now-state graphic; Energiatehokkuus
// (DemandChart) plots the persisted −100…+100 series. Signaalit lists last samples only
// and does not show the score.
//
// Building never computes a threshold, step mapping or fusion rule here — this module only
// projects the already-resolved `ZoneKpi` for display (KB-first: the KB/agent owns the answer).
import type { ZoneKpi, ZoneKpiProvenance, ZoneKpis } from "@/lib/api/agent";
import { formatOccupancyPercent } from "@/components/configuration/occupancy-badge";
import { formatSignalNumericValue } from "@/lib/format-signal-value";

export type ZoneKpiKind = "occupancy" | "air_quality" | "temperature";

// The zone-native data_point id each KPI is ultimately resolved from
// (services/agent/src/agent_domain/knowledge_base/zone_kpi.py) — the seam a raw Signaalit/tile
// row at one of these three ids routes through the shared KPI reader instead of showing its own
// directly-read sample.
export const NATIVE_ID_FOR_ZONE_KPI: Record<ZoneKpiKind, string> = {
  occupancy: "occupancy_count",
  air_quality: "co2",
  temperature: "temperature",
};

const ZONE_KPI_KIND_FOR_NATIVE_ID: Record<string, ZoneKpiKind> =
  Object.fromEntries(
    (Object.entries(NATIVE_ID_FOR_ZONE_KPI) as [ZoneKpiKind, string][]).map(
      ([kind, native_id]) => [native_id, kind],
    ),
  );

// Which KPI (if any) a zone-native data_point id is the raw input for — `null` for every other
// signal (e.g. a non-KPI compare publish), which keeps reading its own raw value as before.
export function zoneKpiKindForNativeId(native_id: string): ZoneKpiKind | null {
  return ZONE_KPI_KIND_FOR_NATIVE_ID[native_id] ?? null;
}

export function zoneKpiOf(
  kpis: ZoneKpis | null | undefined,
  kind: ZoneKpiKind,
): ZoneKpi | null {
  return kpis ? kpis[kind] : null;
}

/** Explicit not-ready triple member — Sisäilmaolosuhteet still renders the row. */
export const ZONE_KPI_UNAVAILABLE: ZoneKpi = {
  ready: false,
  score: null,
  step: null,
  raw_value: null,
  provenance: null,
  confidence: null,
};

export function zoneKpiOfOrUnavailable(
  kpis: ZoneKpis | null | undefined,
  kind: ZoneKpiKind,
): ZoneKpi {
  return zoneKpiOf(kpis, kind) ?? ZONE_KPI_UNAVAILABLE;
}

export function zoneKpiForNativeId(
  kpis: ZoneKpis | null | undefined,
  native_id: string,
): ZoneKpi | null {
  const kind = zoneKpiKindForNativeId(native_id);
  return kind ? zoneKpiOf(kpis, kind) : null;
}

export const ZONE_KPI_NOT_READY_LABEL = "ei saatavilla";
export const ZONE_KPI_STEP_COUNT = 10;
export const ZONE_KPI_SCORE_MIN = -100;
export const ZONE_KPI_SCORE_MAX = 100;

// Product vocabulary for the three indicators — Sisäilmaolosuhteet tiles, Energiatehokkuus
// charts, and Signaalit rows for the persisted `kb_zone_kpi_*` series (the history name is an
// internal id; the user-facing title is this Finnish noun, never a de-underscored English id).
export const ZONE_KPI_LABEL: Record<ZoneKpiKind, string> = {
  occupancy: "Käyttöaste",
  air_quality: "Sisäilman laatu",
  temperature: "Lämpötila",
};

export const ZONE_KPI_AXIS_CAPTION: Record<
  ZoneKpiKind,
  { low: string; high: string }
> = {
  occupancy: { low: "tyhjä", high: "täynnä" },
  air_quality: { low: "ylimäärin", high: "vajaus" },
  temperature: { low: "matala", high: "korkea" },
};

export interface ZoneKpiSourceChip {
  label: "päätelty";
  tone: "learning";
}

// docs/spec/knowledge-base.md § Interface ports and signal roles — the provenance ladder a zone
// KPI carries. Only `inferred` gets a chip, **päätelty**: it is an estimate, not a reading.
// `native`, `equipment-published` and `substituted` are readings and render no chip, so the KPI
// meter keeps its full width (docs/spec/terminology.md § Equipment).
export function zoneKpiSourceChip(kpi: ZoneKpi): ZoneKpiSourceChip | null {
  if (!kpi.ready) return null;
  const provenance: ZoneKpiProvenance | null | undefined = kpi.provenance;
  return provenance === "inferred" ? { label: "päätelty", tone: "learning" } : null;
}

// The raw_value's display unit depends on the KPI: sisäilman laatu / lämpötila are a direct
// physical reading (ppm / °C — the fallback when a caller has no live signal_units entry for
// this zone yet). Käyttöaste's unit depends on its own provenance: a measured headcount for
// native/equipment-published/substituted (unit comes from the caller's own signal_units, same
// as any other headcount channel), a 0.0-1.0 inferred probability for `inferred` — rendered as a
// percentage, the same convention `OccupancyReadOut` already uses (occupancy-badge.ts).
export function formatZoneKpiRawValue(
  kind: ZoneKpiKind,
  kpi: ZoneKpi,
  unit?: string | null,
): string {
  if (!kpi.ready || kpi.raw_value == null) return ZONE_KPI_NOT_READY_LABEL;
  if (kind === "occupancy" && kpi.provenance === "inferred") {
    return formatOccupancyPercent(kpi.raw_value);
  }
  const resolved_unit =
    unit ??
    (kind === "air_quality"
      ? "ppm"
      : kind === "temperature"
        ? "°C"
        : kind === "occupancy"
          ? "persons"
          : undefined);
  return formatSignalNumericValue(kpi.raw_value, resolved_unit);
}

// "luottamus NN %" — only for an inferred reading (a measured/native/substituted/equipment-
// published reading carries no confidence, mirroring OccupancyReadOut's own contract).
export function formatZoneKpiConfidence(kpi: ZoneKpi): string | null {
  if (!kpi.ready || kpi.provenance !== "inferred" || kpi.confidence == null)
    return null;
  return `luottamus ${formatOccupancyPercent(kpi.confidence)}`;
}

// The persisted `data_point_history` name each indicator is written under by the KB tick
// (services/agent/.../adapters/analytics/kb_scheduler.py `_ZONE_KPI_NAMES`, story #1841/#1844) —
// ordinary zone data_point_history rows, discoverable and readable through the existing
// getDataPointHistory client like any other zone signal. Energiatehokkuus → Sisäiset
// vaikuttimet & olosuhteet plots the building service's already-bucketed `zone.kpi` series
// (operational-loop-adapter.ts `kpiCurveFromSeries`). Sisäilmaolosuhteet
// keeps the live now-state badge on the read-time `zoneKpiOf`/`getZoneKpisSummary` path —
// two readers over the same table, never fused (docs/spec/knowledge-base.md § Zone KPI scale).
export const PERSISTED_NAME_FOR_ZONE_KPI: Record<ZoneKpiKind, string> = {
  occupancy: "kb_zone_kpi_occupancy",
  air_quality: "kb_zone_kpi_air_quality",
  temperature: "kb_zone_kpi_temperature",
};

const ZONE_KPI_KIND_FOR_PERSISTED_NAME: Record<string, ZoneKpiKind> =
  Object.fromEntries(
    (
      Object.entries(PERSISTED_NAME_FOR_ZONE_KPI) as [ZoneKpiKind, string][]
    ).map(([kind, name]) => [name, kind]),
  );

/** Which KPI (if any) a persisted `data_point_history` name is the curve for — `null` for every
 *  other name, which is not a zone-KPI point at all. */
export function zoneKpiKindForPersistedName(name: string): ZoneKpiKind | null {
  return ZONE_KPI_KIND_FOR_PERSISTED_NAME[name] ?? null;
}

/** Finnish Signaalit title for a persisted `kb_zone_kpi_*` name, or `null` when `name` is not
 *  one of those three series. */
export function zoneKpiLabelForPersistedName(name: string): string | null {
  const kind = zoneKpiKindForPersistedName(name);
  return kind ? ZONE_KPI_LABEL[kind] : null;
}

export function indexZoneKpisByZone(
  entries: { zone_id: string; kpis: ZoneKpis }[],
): Map<string, ZoneKpis> {
  return new Map(entries.map((e) => [e.zone_id, e.kpis]));
}
